import { Roles } from "@/utils/roles";
import { jwtDecode } from "jwt-decode";

export default {
    methods: {
        /**
         * Check if current user has any of the specified roles
         * @param {string|string[]} requiredRoles - Single role or array of roles
         * @returns {boolean}
         */
        hasRole(requiredRoles) {
            if (!requiredRoles) return true;

            let userRoles = [];

            try {
                // First try to get roles from JWT token
                const token =
                    this.$store.state.auth.accessToken ||
                    localStorage.getItem("accessToken");
                if (token) {
                    const decodedToken = jwtDecode(token);
                    userRoles = decodedToken.role || [];
                }
            } catch (error) {
                console.log(
                    "JWT decode failed in roleAuth mixin, falling back to userData:",
                    error
                );
                // Fallback to userData if JWT decode fails
                userRoles =
                    this.$store.state.auth.userData?.userData?.roles ||
                    this.$store.state.auth.userData?.roles ||
                    [];
            }

            if (Array.isArray(requiredRoles)) {
                return requiredRoles.some((role) => userRoles.includes(role));
            }

            return userRoles.includes(requiredRoles);
        },

        /**
         * Check if current user has all specified roles
         * @param {string[]} requiredRoles - Array of roles
         * @returns {boolean}
         */
        hasAllRoles(requiredRoles) {
            if (!requiredRoles || !Array.isArray(requiredRoles)) return true;

            let userRoles = [];

            try {
                // First try to get roles from JWT token
                const token =
                    this.$store.state.auth.accessToken ||
                    localStorage.getItem("accessToken");
                if (token) {
                    const decodedToken = jwtDecode(token);
                    userRoles = decodedToken.role || [];
                }
            } catch (error) {
                console.log(
                    "JWT decode failed in hasAllRoles, falling back to userData:",
                    error
                );
                // Fallback to userData if JWT decode fails
                userRoles =
                    this.$store.state.auth.userData?.userData?.roles ||
                    this.$store.state.auth.userData?.roles ||
                    [];
            }

            return requiredRoles.every((role) => userRoles.includes(role));
        },

        /**
         * Check if current user is admin or super admin
         * @returns {boolean}
         */
        isAdmin() {
            return this.hasRole([Roles.Admin, Roles.SuperAdmin]);
        },

        /**
         * Check if current user is super admin
         * @returns {boolean}
         */
        isSuperAdmin() {
            return this.hasRole(Roles.SuperAdmin);
        },

        /**
         * Check if current user can perform CRUD operations on a resource
         * @param {string} resource - Resource name (e.g., 'User', 'News', 'Project')
         * @param {string} operation - Operation type ('Create', 'Read', 'Update', 'Delete')
         * @returns {boolean}
         */
        canPerformOperation(resource, operation) {
            const roleKey = `${resource}${operation}`;
            return this.hasRole(Roles[roleKey]) || this.isAdmin();
        },

        /**
         * Get current user roles from JWT token
         * @returns {string[]} Array of user roles
         */
        getCurrentUserRoles() {
            try {
                const token =
                    this.$store.state.auth.accessToken ||
                    localStorage.getItem("accessToken");
                if (token) {
                    const decodedToken = jwtDecode(token);
                    return decodedToken.role || [];
                }
            } catch (error) {
                console.log(
                    "JWT decode failed in getCurrentUserRoles, falling back to userData:",
                    error
                );
                // Fallback to userData if JWT decode fails
                return (
                    this.$store.state.auth.userData?.userData?.roles ||
                    this.$store.state.auth.userData?.roles ||
                    []
                );
            }
            return [];
        },

        /**
         * Show unauthorized message and redirect
         */
        showUnauthorized() {
            this.showError("ليس لديك صلاحية للوصول إلى هذه الصفحة");
            this.$router.push("/forbidden");
        },
    },
};
